import { useNavigate } from 'react-router-dom';
import { Button, Panel, secondaryTextClassName } from '@monecolier/ui';
import type { EnrollmentReviewEntry } from '@monecolier/api-client';
import { CLASS_LEVEL_LABELS, type ClassLevel as ClassLevelType } from '@monecolier/types';
import { toneBadge } from '../lib/classCapacityStatus';

/**
 * UX-44 — EXTRAIT de `StudentsOverview.tsx` (UX-18, seule implémentation
 * avant ce ticket) : `Home.tsx` en devient le SECOND consommateur réel,
 * jamais une seconde présentation divergente des mêmes demandes PENDING.
 * Reçoit ses données EN PROPS — l'appelant décide de l'appel réseau (`GET
 * /enrollments/review?status=PENDING`).
 *
 * UX-67 : "Voir toutes les demandes" était un lien texte (`linkClassName`)
 * en action principale du panneau — converti en `<Button>` (voir
 * docs/history/UX.md, section "UX-67").
 */
export function RecentEnrollmentRequestsPanel({
  entries,
  titleAs,
}: {
  entries: EnrollmentReviewEntry[];
  // UX-74 : deux contextes de page réels, même raisonnement que
  // ClassHeadcountPanel.tsx ci-dessus (défaut h2 sur StudentsOverview.tsx,
  // h3 explicite sous le <h2> "Bonjour" de Home.tsx).
  titleAs?: 'h2' | 'h3' | 'h4';
}) {
  const navigate = useNavigate();
  return (
    <Panel title="Dernières demandes" titleAs={titleAs}>
      {entries.length === 0 ? (
        <p className={secondaryTextClassName} data-testid="recent-requests-empty">
          Aucune demande en attente.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {entries.map((entry) => (
            <li
              key={entry.id}
              data-testid={`recent-request-${entry.id}`}
              className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2 last:border-0 last:pb-0"
            >
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {entry.student.firstName} {entry.student.lastName}
                </p>
                <p className={secondaryTextClassName}>
                  {CLASS_LEVEL_LABELS[entry.requestedLevel as ClassLevelType] ?? entry.requestedLevel} · parent :{' '}
                  {entry.requester.fullName}
                </p>
              </div>
              {toneBadge('En attente', 'amber')}
            </li>
          ))}
        </ul>
      )}
      <Button
        type="button"
        variant="primary"
        size="small"
        className="mt-3 w-full"
        onClick={() => navigate('/enrollments')}
        data-testid="recent-requests-see-all"
      >
        Voir toutes les demandes
      </Button>
    </Panel>
  );
}
